import { useEffect, useState } from "react";
import { Check, Laptop2, Link as LinkIcon } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { savePairedDevice } from "@/lib/pairedDevice";

function deviceLabel(name) {
  return name?.trim() || "Windows Laptop";
}

export default function ConnectDevicePage() {
  const [searchParams] = useSearchParams();
  const [pairedDevice, setPairedDevice] = useState(null);
  const deviceKey = (searchParams.get("device") || "").trim();
  const deviceName = deviceLabel(searchParams.get("name"));
  const isValid = deviceKey.length >= 8;

  useEffect(() => {
    document.title = "Runzr - Connect";
  }, []);

  useEffect(() => {
    if (!isValid) return;
    setPairedDevice(savePairedDevice(deviceKey, deviceName));
  }, [deviceKey, deviceName, isValid]);

  return (
    <div
      className="min-h-screen px-4 py-6 sm:px-6"
      style={{
        background:
          "radial-gradient(circle at top, rgba(16,185,129,0.14), transparent 28%), #0A0A0A",
      }}
    >
      <div className="mx-auto w-full max-w-md">
        <div className="mb-5">
          <p
            className="text-xs uppercase tracking-[0.32em]"
            style={{ color: "#71717A", fontFamily: "'JetBrains Mono', monospace" }}
          >
            Runzr
          </p>
          <h1 className="mt-2 text-3xl font-semibold text-white">Connect Laptop</h1>
          <p className="mt-2 text-sm leading-6" style={{ color: "#A1A1AA" }}>
            Save this Windows machine to your phone once, then future approvals can open from the same Runzr page.
          </p>
        </div>

        <div
          className="rounded-[28px] p-5 shadow-2xl"
          style={{
            background: "rgba(18,18,18,0.96)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {isValid && pairedDevice ? (
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div
                  className="mt-1 rounded-2xl p-3"
                  style={{ background: "rgba(16,185,129,0.16)", color: "#10B981" }}
                >
                  <Check className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-lg font-semibold text-white">Phone connected</p>
                  <p className="mt-1 text-sm leading-6" style={{ color: "#A1A1AA" }}>
                    Runzr will now remember this laptop in this browser.
                  </p>
                </div>
              </div>

              <section
                className="rounded-2xl p-4"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <div className="flex items-center gap-3">
                  <Laptop2 className="h-4 w-4" style={{ color: "#10B981" }} />
                  <div>
                    <p className="text-sm font-medium text-white">{pairedDevice.deviceName}</p>
                    <p
                      className="mt-1 text-xs"
                      style={{ color: "#71717A", fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      device={pairedDevice.deviceKey.slice(0, 12)}...
                    </p>
                  </div>
                </div>
              </section>

              <div className="grid gap-3">
                <Link
                  to="/approve"
                  className="rounded-2xl px-4 py-4 text-center text-sm font-medium"
                  style={{ background: "#FFFFFF", color: "#090909" }}
                >
                  Open approvals
                </Link>
                <p className="text-xs leading-6" style={{ color: "#71717A" }}>
                  On your laptop, run <code>python -m agent.runzr run -- &lt;command&gt;</code>. This phone page will
                  now watch that same device automatically.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div
                  className="mt-1 rounded-2xl p-3"
                  style={{ background: "rgba(245,158,11,0.12)", color: "#F59E0B" }}
                >
                  <LinkIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-lg font-semibold text-white">Missing connect link</p>
                  <p className="mt-1 text-sm leading-6" style={{ color: "#A1A1AA" }}>
                    Open the connect URL generated by your Windows laptop to pair this browser.
                  </p>
                </div>
              </div>

              <section
                className="rounded-2xl p-4 text-sm"
                style={{ background: "rgba(255,255,255,0.03)", color: "#D4D4D8" }}
              >
                Run this on your laptop:
                <pre
                  className="mt-3 overflow-x-auto whitespace-pre-wrap break-words rounded-xl p-3 text-xs"
                  style={{
                    background: "#090909",
                    color: "#E4E4E7",
                    fontFamily: "'JetBrains Mono', monospace",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  python -m agent.runzr connect --base-url https://www.runzr.com
                </pre>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
